/*
   This file is part of Universal Sequencer.
    Universal Sequencer is free software: you can redistribute it and/or modif
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Universal Sequencer is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import Tone from 'tone';
import Sample from 'repos/Sample.js';
import {setPullablePartDragging} from 'actions/index.js';
import SamplesStream from './SamplesStream.js';
import Effects from 'effects/Effects.js';
import {trackFactory} from './TrackFactory.js';
import EffectsChain from 'effects/EffectsChain.js';
import LostParameters from "utils/LostParameters.js";
import {
    setTrackMidiEffects,
    setLoadingText,
    deleteAutomationsForParameters
} from 'actions/index.js';
import MidiEffectsChain from 'effects/midi/MidiEffectsChain.js';
import NodeMessaging from "messaging/NodeMessaging.js";
import {TRACK} from 'constants/object-types.js';
import {NOT_UPLOADED_YET} from 'constants/ipfs-constants.js';
import {uuid} from 'uuidv4';
import {onVolumePatternChange, onParameterPatternChange} from 'parameters/onPatternChange.js';
import LiveEffectsChain from 'effects/live/LiveEffectsChain.js';
import {getDefaultEffects} from 'effects/getDefaultEffects.js';
import RollableObject from './RollableObject.js';
import DelayEffect from 'effects/Delay.js';
import ParameterContext from 'effects/ParameterContext.js';
import {EFFECTS} from 'constants/editor-bottom-modes.js';
import Resampler from './Resampler.js';
import TrackVolumeChangeEvent from 'session/TrackVolumeChangeEvent.js';
import {saveParameterToJson, loadParameterFromJson} from 'parameters/parameterJsonFunctions.js';
import {AudioParamValueParameter} from 'parameters/AbstractAudioParamParameter.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import Chokes from './Chokes.js';
import WarpEffect from 'effects/midi/WarpEffect.js';
import {toggleEffect, addEffect} from 'operations/actions.js';
import ipfs from 'decentralized/Ipfs.js';
import {MASTER, SENDS_ONLY} from 'constants/track-output-types.js';
import SoundUploader from 'api/SoundUploader.js';
import TimingEffect from 'effects/midi/TimingEffect.js';
import {TRIGGER} from 'messaging/message-types.js';
import {getVolume, getGainVolume} from 'utils/getVolume.js';
import {extractGroove} from 'utils/extractGroove.js';
import TrackMidiInputs from './TrackMidiInputs.js';
import TrackSendVolumeChangeEvent from 'session/TrackSendVolumeChangeEvent.js';
import TrackMuteEvent from 'session/TrackMuteEvent.js';
import TrackSoloEvent from 'session/TrackSoloEvent.js';
import {
    getStepGroupJson,
    fromStepGroupJson
       } from 'pattern/groups/stepGroups.js';
import TrackReverseEvent from 'session/TrackReverseEvent.js';
import {isNumber} from 'utils/IsNumber.js';
import {
    setSample,
    setTrackIsLoading, 
    setViMode,
    setEffectParameterValues,
    setAllTrackSteps,
    setShowTrackEffects,
    setEffectsChainToShow,
    setTrackBrowsedSamples,
    setTrackSteps,
    setTrackPianoRollSteps,
    setTrackFills,
    setTrackVolume,
    setTrackPanning,
    setTrackSendVolume,
    setTrackMuted,
    setTrackSolo,
    setSampleAuditioning,
    setTrackIsEnabled,
    setTrackPatternLocked,
    setTrackPatternLocks,
    setTrackFrozen,
    setFrozenTrackPlaying,
    setTrackEnvelopes
       } from 'actions/index.js';
import {INSTRUMENT} from 'constants/track-types.js';
import {RECORD} from 'constants/automation-modes.js';
import axios from 'axios';
import Pattern from 'pattern/Pattern.js';
import EasyPattern from 'pattern/EasyPattern.js';
import StepData from 'pattern/StepData.js';
import Sampler from 'instruments/Sampler.js';
import Resolution from 'engine/Resolution.js';
import ipfsClient from 'decentralized/Ipfs.js';
import ArpManager from 'keyboard/ArpManager.js';
import {SAMPLER} from 'constants/track-types.js';
import {moveKey, deleteKey} from 'utils/objectMover.js';
import {ROLL_EFFECT, ALWAYS_ON, ALWAYS_OFF, SYNCED} from 'effects/live/constants.js';
import EnvelopeConnection from 'modulators/EnvelopeConnection.js';
import {PRE_FX, POST_FX} from 'modulators/constants.js';
import ethereum from 'decentralized/ethereum.js';
import TriggerableTrack from './TriggerableTrack.js';
import Mappable from './Mappable.js';
import RackUtils from "effects/rack/RackUtils.js";
import ColorableTrack from './ColorableTrack.js';

const presets = ethereum.presets;

const baseResolution = new Resolution(32, false);
const oldDefaultResolution = new Resolution(16, false);

const MUTED_VOLUME = -125;
const UNMUTED_VOLUME = 0;
let id = 0;

class Track extends RollableObject(NodeMessaging(LostParameters(RackUtils(Mappable(ColorableTrack(TriggerableTrack(class{}))))))) {
    constructor(trackNumber, sequencerEngine, mixSession, numberOfPatterns, update, subSequence) {
        super();
        this.subSequence = subSequence;
        this.id = uuid();
        this.trackNumber = trackNumber;
        this.gated = true;

        this.sequencerEngine = sequencerEngine;

        this.sequencerEngine.idToObject[this.id] = this;
        
        // redux store
        this.store = sequencerEngine.store;

        this.parameters = [];

        // update is the callback to be used to update the UI
        this.update = update;

        // the track chain is as follows:
        // gainNode -> fantomDelayNode -> effects -> muteableNode -> sends & mix
        // soleableNode -> trackNode -> trackMeter

        // the volume node for the entire track, comes before the fantomDelayNode
        let {gainNode, volumeNode, muteableNode, trackNode, trackMeter, faderNode} =
            trackFactory.useTrack();
        this.gainNode = gainNode;
        this.volumeNode = volumeNode;
        this.muteableNode = muteableNode;
        this.trackNode = trackNode;
        this.trackMeter = trackMeter;
        this.faderNode  = faderNode;

        this.volumeParameter = new AudioParamValueParameter(
            'volume', this.volumeNode.volume, {
                defaultValue: 0,
                prettyName: "Track Volume",
                min: -60,
                max: 6,
                isDb: true,
                onChange: x => {
                    if (this.pad) {
                        this.pad.sendSliderLevels();
                    }
                }
            },
            0.1);
        this.registerParameter(this.volumeParameter);

        // gainNode -> effects -> trackNode -> volumeNode -> muteableNode 

        this.trackNode.connect(this.volumeNode);
        this.volumeNode.connect(this.faderNode);
        this.faderNode.connect(this.muteableNode);
        this.muteableNode.connect(this.trackMeter);
        this.muted = false;

        // list of send nodes available to the track
        this.sendNodes = [];
        this.sendNodeVolumeParameters = [];
        
        // Set up patterns
        this.patternNumberLocked = undefined;
        this.pattern = new Pattern(this.sequencerEngine, this);
        this.patterns = [this.pattern];
        for (var i = 1; i < numberOfPatterns; i++) {
            this.patterns.push(new Pattern(this.sequencerEngine, this));
        }
        this.currentPattern = this.getInitialPattern();

        // keep track of sound ids and what sounds are being auditioned
        this.soundIds = [];
        this.auditionedSounds = [];
        this.toAudition = [];
        
        // all steps in a track by default use this trackData object.
        // But when a specific step is edited then, that step's stepData is used
        this.trackData = new StepData(
            this.sequencerEngine,
            this.mix,
            new ParameterContext().forTrack(this));
        this.trackData.loadDefaults();

        this.on = false;

        // keeps track of what samplers are hooked up
        this.samplers = {};

        // the group
        this.group = null;

        this.selectedStep = undefined;

        this.name = undefined;
        this.videoId = '';

        this.resampler = undefined;
        this.masterResampler = new Resampler(this.sequencerEngine, this.volumeNode);

        // a track may have multiple parallel effects chains
        this.effectsChains = [
            new EffectsChain(this.gainNode, this.trackNode,
                             new ParameterContext().forTrack(this),
                             this.sequencerEngine)];

        // we start with one effects chain
        this.effectsChains[0].initDefault();

        this.midiEffects = new MidiEffectsChain(this);
        this.liveEffects = new LiveEffectsChain(this);

        this.midiInputs = new TrackMidiInputs();

        this.samples = [];

        this.connectedEnvelopes = [];
        this.frozenSamplesToSave = [];

        this.chokes = new Chokes(this);

        this.outputTypeConfigured = MASTER;
        this.outputTypeParameter = new AbstractParameter(
            'outputType',
            this,
            (o, outputType) => this.changeOutputType(outputType),
            {
                defaultValue: MASTER,
                automatable: false
            });
        this.registerParameter(this.outputTypeParameter);

        this.skipMidiMasterParameter = new AbstractParameter(
            'skipMidiMaster',
            this,
            (o, x) => [],
            {
                defaultValue: false,
                automatable: false
            });
        this.registerParameter(this.skipMidiMasterParameter);

        this.newFrozenColumn();

        this.disabled = false;

        this.stepGroups = [];

        this.newInlet("trigger ", ([msg]) => this.onTrigger(msg));
        this.newInlet("audition sample", ([sample]) => this.loadAndUseNewSound(sample));
        this.newInlet("restart", ([x]) => this.onRestart(x));
        this.newOutlet(TRIGGER);
        this.newOutlet("levels data");
        this.newOutlet("pattern progress");
    }

    onTrigger(stepData) {
        let wasBang = stepData === "bang";
        if (stepData === 'bang' || stepData.transpose === undefined) {
            stepData = this.trackData;
        }
        let time = stepData.time;
        let stepNumber = this.sequencerEngine.stepNumber;
        stepData = this.midiEffects.getStep(stepData);
        stepData.transpose += this.trackData.transpose;
        stepData.velocity *= this.trackData.velocity;
        stepData.duration *= this.trackData.duration;
        stepData.track = this;
        if (wasBang) {
            this.liveTrigger(stepData, 0, Tone.Master.context.currentTime+0.001);
        } else {
            this.midiEffects.receivedTriggers.push({
                time, stepNumber, stepData});
        }
    }

    getObjectType() {
        return TRACK;
    }

    get skipMidiMaster() {
        return this.skipMidiMasterParameter.get();
    }

    // put in mixin!
    changeOutputType(type) {
        if (this.outputTypeConfigured === type) {
            return;
        }

        this.outputTypeConfigured = type;

        if (type === SENDS_ONLY) {
            this.muteableNode.disconnect();
            this.reconnectAll();
        } else {
            this.addToGroup(this.group);
        }
    }

    registerParameter(parameter, addToList=true) {
        if (addToList) {
            this.parameters.push(parameter);
        }
        parameter.setStore(this.store);
        parameter.setContext(new ParameterContext().forTrack(this));
    }

    newEffectsChain() {
        let fxChain = new EffectsChain(
                this.gainNode,
                this.trackNode,
                new ParameterContext().forTrack(this),
                this.sequencerEngine);
        fxChain.initDefault();
        this.effectsChains.push(fxChain);
        this.store.dispatch(setEffectsChainToShow(
            this.subSequence.id,
            this.trackNumber,
            this.effectsChains.length - 1));
    }

    deleteEffectsChain() {
        let fxChainToDelete = this.effectsChains[this.chainNumber];
        if (fxChainToDelete === undefined) {
            return;
        }

        fxChainToDelete.dispose();
        this.effectsChains.splice(this.chainNumber, 1);

        this.store.dispatch(setEffectsChainToShow(
            this.subSequence.id,
            this.trackNumber,
            this.effectsChains.length - 1));
    }

    getEffects(chainNumber=this.chainNumber) {
        return this.effectsChains[chainNumber].effects;
    }

    connectToEnvelope(env, mode=POST_FX) {
        // check if the envelope is already connected to a track
        for (let i in this.sequencerEngine.tracks) {
            let track = this.sequencerEngine.tracks[i];
            if (track.isConnectedToEnvelope(env)) {
                track.disconnectFromEnvelope(env);
            }
        }
        this.connectedEnvelopes.push(new EnvelopeConnection(env, mode));
        this.store.dispatch(setTrackEnvelopes(this.id, [... this.connectedEnvelopes]));
    }

    disconnectFromEnvelope(env) {
        for (let i=0; i < this.connectedEnvelopes.length; i++) {
            if (this.connectedEnvelopes[i].envelope === env) {
                this.connectedEnvelopes.splice(i, 1);
                this.store.dispatch(setTrackEnvelopes(this.id, [... this.connectedEnvelopes]));
                return;
            }
        }
    }

    getEnvelopeMode(envelope) {
        return this.connectedEnvelopes.filter(
            x => x.envelope === envelope)
            .map(x => x.mode)[0];
    }

    setEnvelopeMode(envelope, mode) {
        return this.connectedEnvelopes.filter(
            x => x.envelope === envelope)
            .forEach(x => x.mode = mode);
    }

    isConnectedToEnvelope(env) {
        return this.connectedEnvelopes.some(
            x => x.envelope === env);
    }

    get rolling() {
        let rollEffect = this.liveEffects.getEffect(ROLL_EFFECT);
        if (rollEffect) {
            switch (rollEffect.mode) {
            case ALWAYS_ON:
                return true;
            case ALWAYS_OFF:
                return false;
            default:
                return this.sequencerEngine.rolling;
            }
        } else {
            return this.sequencerEngine.rolling;
        }
    }

    start() {
        this.restartedAtStepNumber = undefined;
        this.lastPatternStep = undefined;
        this.effectsChains.forEach(chain => chain.start());
        this.midiEffects.clear();
        this.liveEffects.clear();

        // refresh any parameters connected to lfos
        if (this.getTrackType() === INSTRUMENT) {
            let instrument = this.getInstrument();
            if (instrument && instrument.hasLFO()) {
                instrument.stop();
                instrument.start();
            }
        }

        this.notesTriggered = {};
        this.accumulators = {};
        this.patterns.forEach(s => s.isRecording = false);
        this.scheduleTrackPatternChangeIfAny();

        for (let group of this.stepGroups) {
            group.transformations.forEach(
                t => t.loopCounter = -1);
        }
    }

    getGroupForId(id) {
        return this.stepGroups.find(x => x.id === id);
    }

    get patternLocked() {
        let subSequenceLocks = this.store.getState()
            .tracks.trackPatternLocks[this.subSequence.id];
        if (subSequenceLocks === undefined) {
            return false;
        }

        let patterns = subSequenceLocks[this.trackNumber] || {};
        return patterns[this.currentPattern] || false;
    }

    linearRampFade(fromValue, toValue, time) {
        this.faderNode.gain.linearRampTo(fromValue, 0.001, Tone.Master.context.currentTime + 0.001);
        this.faderNode.gain.linearRampTo(toValue, time, Tone.Master.context.currentTime + 0.003);
    }

    set fade(x) {
        let volume = getGainVolume(x, 0, 1, 1, 0);
        this.faderNode.gain.setValueAtTime(volume, 0.0001);
    }

    set patternLocks(patternLocks) {
        for (let pattern in patternLocks) {
            this.store.dispatch(
                setTrackPatternLocked(
                    this.subSequence.id, this.trackNumber, pattern, patternLocks[pattern]));
        }

        this.getAllPatternLockedParameters()
            .forEach(parameter => parameter.updateRedux());
    }

    deletePatternLocks() {
        this.getAllParameters()
            .forEach(parameter => parameter.deleteLocks());

        this.store.dispatch(setTrackPatternLocks(this.subSequence.id, this.trackNumber, {}));
    }

    deleteAutomations() {
        this.getAllPatternLockedParameters()
            .forEach(parameter => parameter.deleteAutomations());
    }

    togglePatternLock() {
        this.store.dispatch(
            setTrackPatternLocked(
                this.subSequence.id,
                this.trackNumber,
                this.currentPattern,
                !this.patternLocked));
        this.getAllPatternLockedParameters()
            .forEach(parameter => parameter.updateRedux());
    }

    getName(samplerNumber) {
        if (samplerNumber !== undefined) {
            return this.samples[samplerNumber].name;
        }
        return this.sample === undefined ? '' : this.sample.name;
    }

    isRecordable() {
        return false;
    }

    setSequencerEngine(sequencerEngine) {
        this.sequencerEngine = sequencerEngine;
    }

    /*
    updatePatternRollStepNumber(resolution) {
        if (this.patternRollStepNumber === undefined) {
            return;
        }
        let numSteps = resolution.getNumberOfStepsAtBaseResolution();
        this.patternRollStepNumber -= this.patternRollStepNumber % numSteps;
    }
    */

    get resolution() {
        return this.sequencerEngine.resolution;
    }

    getFactoredResolution() {
        let resolution = this.sequencerEngine.rollingResolution;
        let rollEffect = this.liveEffects.getEffect(ROLL_EFFECT);
        if (rollEffect) { 
            resolution = resolution.getCopy();
            resolution.resolution *= rollEffect.rollFactor;
        }
        return resolution;
    }

    solo(solo, automation=true, updateRedux=true) {
        this.trackNode.solo = solo;
        // bug where solo gets confused, so we force set
        // all the solo nodes
        //this.addEvent(new TrackSoloEvent(this, solo), automation);
        if (updateRedux) {
            this.store.dispatch(setTrackSolo(this.id, solo));
        }
    }

    getSolo() {
        return this.trackNode.solo;
    }

    get mix() {
        if (this.state.sequencer.isRecording &&
            this.state.sequencer.automationMode === RECORD)  {
            return this.pattern.mix;
        }
        return this.subSequence.mix;
    }

    dispose(disposeParameters=true) {
        if (this.levelsInterval) {
            clearInterval(this.levelsInterval);
        }
        this.sequencerEngine.dispatch(
            setPullablePartDragging(undefined));
        let parameters = [];

        this.midiEffects.dispose();

        if (disposeParameters) {
            parameters = this.getAllParameters();
            parameters.forEach(
                p => p.isMapped && p.isMapped.unmap(p));
        }

        let automationParameters = parameters
            .filter(x => x.automations && x.automations.length > 0);
        this.sequencerEngine.store.dispatch(
            deleteAutomationsForParameters(automationParameters));
        
        trackFactory.releaseTrack(
            {
                gainNode: this.gainNode,
                volumeNode: this.volumeNode,
                muteableNode: this.muteableNode,
                trackNode: this.trackNode,
                trackMeter: this.trackMeter,
                faderNode: this.faderNode
            });
                
        this.sendNodes.forEach(s => trackFactory.releaseSend(s));

        this.effectsChains.forEach(e => e.dispose());
        if (this.isFrozen) {
            this.disposeFreezes();
        } else {
            this.getSamplers()
                .filter(s => s !== undefined)
                .forEach(s => s.dispose());
        }

        this.faderNode = undefined;
        this.gainNode = undefined;
        this.muteableNode = undefined;
        this.trackNode = undefined;
        this.trackMeter = undefined;
        this.sendNodes = undefined;
        this.pattern = undefined;
        this.patterns.forEach(
            p => p.dispose());
        this.patterns = undefined;
        this.soundIds = undefined;
        this.auditionedSounds = undefined;
        this.trackData = undefined;
        this.samplerConnectedToMaster = false;
        this.samplers = undefined;
        this.group = null;
        this.selectedStep = undefined;
        this.name = undefined;
        this.videoId = undefined;

        if (this.sample) {
            this.sample.dispose(true);
        }
        this.presetId = undefined;
        if (this.samplesStream) {
            this.samplesStream.dispose();
            this.samplesStream = undefined;
        }
        if (disposeParameters) {
            parameters
                .filter(p => p !== this.volumeParameter)
                .filter(p => !this.sendNodeVolumeParameters.includes(p))
                .forEach(p => p.dispose());
        }
        this.parameters = undefined;

    }

    getTrackType() {
        return SAMPLER;
    }

    addToGroup(trackGroup) {
        if (this.outputTypeParameter.get() === MASTER) {
            this.muteableNode.connect(trackGroup.inputNode);
        }
        this.group = trackGroup;
    }

    disconnectFromGroup() {
        this.muteableNode.disconnect();
        this.reconnectAll();
        this.group = undefined;
    }

    getLoadedSampleIfAny(id) {
        return this.sequencerEngine.loadedSamples[id];
    }

    async addSound(sample, samplerData, showInfo, updateLoading=true) {
        if (updateLoading) {
            this.store.dispatch(setTrackIsLoading(this.subSequence.id, this.trackNumber, true));
        }

        // todo: remove the number shit since theres no number samples
        if (typeof sample === 'string' || typeof sample === 'number') {
            let sampleId = sample;
            this.soundIds.push(sampleId);
            if (this.getLoadedSampleIfAny(sampleId) !== undefined) {
                sample = this.getLoadedSampleIfAny(sampleId);
                if (this.sample === undefined) {
                    this.sample = sample;
                }
            } else {
                sample = new Sample(sampleId);
                if (this.sample === undefined) {
                    this.sample = sample;
                }
                try {
                    if (sample.getId() !== NOT_UPLOADED_YET) {
                        await sample.load();
                    }
                } catch (e) {
                    console.log('error sample ', e);
                }
            }
        } else {
            if (this.sample === undefined) {
                this.sample = sample;

            }
            if (!this.sample.isLoaded() && sample.getId() !== NOT_UPLOADED_YET) {
                try {
                    await this.sample.load()
                } catch(e) {
                    console.log('error in track load');
                    return;
                }
            }
            this.soundIds.push(sample.getId());
        }

        if (!sample.isLoaded()) {
            // dont add an inaccessible sample to the track
            return;
        }


        this.samples.push(sample);

        showInfo = showInfo || false;

        this.loadTrackInfo(showInfo, sample, samplerData, updateLoading);
        this.samplesStream = new SamplesStream(sample, this.sequencerEngine);
        setTimeout(() => {
            if (!this.samplesStream.sounds || this.samplesStream.sounds.length === 0) {
                this.samplesStream.search();
            }
        }, 300);
        if (updateLoading) {
            this.store.dispatch(setTrackIsLoading(this.subSequence.id, this.trackNumber, false));
        }
        this.addSoundToBrowsedSamples(sample);
    }

    get trackIsLoading() {
        let loading = this.sequencerEngine.state.tracks.trackIsLoading[this.subSequence.id] &&
            this.sequencerEngine.state.tracks.trackIsLoading[this.subSequence.id][this.trackNumber];
        return loading;
    }
    
    set trackIsLoading(loading) {
        if (this.sequencerEngine.isOffline) {
            return;
        }
        this.store.dispatch(setTrackIsLoading(this.subSequence.id, this.trackNumber, loading));
    }

    // called by addSound for loading the sound name/video and actual
    // audio file
    loadTrackInfo(showInfo, sample, samplerData, updateLoading=true) {
        if (samplerData === undefined) {
            samplerData = {
                slices: undefined,
                offset: 0,
                transpose: 0,
                volume: 0
            };
        }
        showInfo = showInfo || true;
        var self = this;

        this.store.dispatch(setSample(sample.getId(), sample.getJson()));
        if (showInfo) {
            // load the name and videoId into the track
            this.videoId = sample.videoId;
            this.sample = sample;
            if (this.videoId === undefined && this.getTrackType() !== 'MIDI') {
                this.name = sample.name;
            }
            this.update();
        } else {
            this.name = sample.name;
            this.update();
        }
        
        let slices = samplerData.slices;

        // instantiate the Sampler with the soundId and data, and upon finishing loading
        // call onNewSampler
        this.samplers[sample.getId()] = new Sampler(
            this,
            sample, (s) => this.onNewSampler(self, s), slices,
            samplerData);

        this.samplers[sample.getId()].init();
        this.samplers[sample.getId()].sliceFirstOnset(false);

        sample.addedToProject = true;
        if (updateLoading) {
            this.store.dispatch(setTrackIsLoading(this.subSequence.id, this.trackNumber, false));
        }
    }

    get chainNumber() {
        let state = this.store.getState();
        let subSequenceChains = state.effects.effectsChainToShow[this.subSequence.id] || {};
        let chainNumber = subSequenceChains[this.trackNumber] || 0;
        return chainNumber;
    }

    get effectsChain() {
        return this.effectsChains[this.chainNumber];
    }

    get effects() {
        if (this.effectsChains[this.chainNumber] === undefined) {
            return [];
        }
        return this.effectsChains[this.chainNumber].effects;
    }

    updateEffects(effects) {
        if (effects === undefined) {
            effects = this.effects;
        }
        this.effectsChains[this.chainNumber].updateEffects(effects);
    }

    // Called when a Sampler has been instantiated.
    // Loads the effects, connects to a Tone gainNode (the output node for the track)
    // and sets the volume for the track
    onNewSampler(self, sampler) {
        for (let id in this.samplers) {
            if (sampler === this.samplers[id] &&
                this.toAudition.indexOf(id) > -1 &&
                this.isWarped()) {
                let index = this.toAudition.indexOf(id);
                this.toAudition.splice(index, 1);
                sampler.sliceFirstOnset();
                break;
            }
        }

        sampler.gainNode.connect(this.gainNode);
        // connect the effects (currently there are none, it'll just connect to the master node)
        self.switchToPattern(self.currentPattern);
        // set volume to value save
        self.setVolume(self.getVolume(), true, true);
        self.setPanning(self.getPanning(), undefined, true);
    }

    // functions for configuring the Sends of a track
    setSends(sends) {
        this.sends = sends;
        this.sends.getSends().map((send) =>
                                  this.newSend(send));
    }

    setSendVolume(sendNumber, volume, automation=false) {
        this.sendNodeVolumeParameters[sendNumber].set(volume, automation);
        this.store.dispatch(setTrackSendVolume(this.trackNumber, sendNumber, volume));
    }

    setupEffects() {
        let effectsChain = this.effectsChains[this.chainNumber];
        if (effectsChain) {
            effectsChain.setupEffects();
        }
    }

    // creates a new send on the track
    newSend(send) {
        var sendNode = trackFactory.useSend(); //new Tone.Volume(-100);
        this.muteableNode.connect(sendNode);
        send.connectToTrack(sendNode);
        this.sendNodes.push(sendNode);
        let sendNodeVolumeParameter = new AudioParamValueParameter(
            'send volume', sendNode.volume, {
                defaultValue: -90,
                prettyName: "Send " + send.name,
                min: -90,
                max: 0,
                isDb: true
            });
        this.registerParameter(sendNodeVolumeParameter);
        this.sendNodeVolumeParameters.push(sendNodeVolumeParameter);
    }

    reconnectAll() {
        this.sendNodes.forEach(
            sendNode => this.muteableNode.connect(sendNode));
        this.muteableNode.connect(this.trackMeter);
    }

    toggleEnabled() {
        this.setIsEnabled(!this.isEnabled);
    }

    setIsEnabled(isEnabled) {
        this.isEnabled = isEnabled;
        if (this.isEnabled) {
            this.store.dispatch(setViMode(false));
        }
        this.store.dispatch(setTrackIsEnabled(
            this.subSequence.id,
            this.id,
            this.isEnabled));
    }

    getSendNodes() {
        return this.sendNodes;
    }

    // getters for Sampler objects tied to this track
    getSampler(i) {
        if (this.isFrozen) {
            return this.frozenColumns[0].frozenSamplers[0];
        }
        i = i || 0;
        if (this.samplers === undefined || this.soundIds === undefined) {
            return undefined;
        }
        return this.samplers[this.soundIds[i]];
    }

    getSamplers() {
        if (this.isFrozen) {
            let samplesFromColumns = this.frozenColumns.map(
                col => col.frozenSamplers[col.playing])
                .filter(col => col !== undefined);
            return samplesFromColumns;
        }
        return this.soundIds.map(id => this.samplers[id]);
    }

    deleteSound(soundId) {
        var index = this.soundIds.indexOf(soundId);
        if (index > -1) {
            this.soundIds.splice(index, 1);
            delete this.samplers[soundId];
            this.soundId = this.soundIds[0];
        }
    }

   getVolume() {
       return this.pattern ? this.pattern.volume : 0;
    }

    getMuted() {
        return this.pattern ? this.pattern.isMuted : false;
    }

    async newPattern(copy) {
        let a = new Date().getTime();
        copy = copy === undefined ? false : copy;
        if (copy) {
            let newPattern = this.pattern.getCopy();
            this.patterns.push(newPattern);
            let c = new Date().getTime();

            // update all the pattern locks so that the new pattern is also locked (if
            // this one is locked)
            let newPatternNumber = this.patterns.length - 1;
            let parameters = this.getAllPatternLockedParameters();
            parameters
                .forEach(parameter => parameter.newPattern(this.currentPattern, this.patterns.length - 1));
            let d = new Date().getTime();
            
            if (this.patternLocked) {
                this.store.dispatch(setTrackPatternLocked(
                    this.subSequence.id, this.trackNumber, this.patterns.length - 1, this.patternLocked));
            }
        } else {
            this.patterns.push(new Pattern(this.sequencerEngine, this));
        }

        let b = new Date().getTime();
    }

    // switches the track to a given pattern number
    switchToPattern(i, ignorePatternLocked=false, time=0.001) {
        let name = this.presetName || this.getName();
        if (i >= 0 && i < this.patterns.length) {
            let pattern = this.patterns[i];
            let volume = pattern.volume;
            let muted = pattern.muted;
            let oldPattern = this.pattern;

            this.pattern = pattern;
            this.currentPattern = i;
            let oldPatternNumber = this.patterns.indexOf(oldPattern);

            let volLocked = !this.attributes || (this.attributes["volume pattern-locked"] !== undefined &&
                                                 !this.attributes["volume pattern-locked"]);
            let volumeToBulk = volLocked ? onVolumePatternChange(this, oldPatternNumber, volume, time) : {};

            let muteToBulk = this.mute(muted, true, true);

            // todo: batch this by doing this in one project wide action
            let parameters = this.getAllPatternLockedParameters(this.patterns.indexOf(oldPattern), i);
            let bulkUpdate = {};
            let ids = new Set();


            for (let i in parameters) {
                let parameter = parameters[i];
                if (ids.has(parameter.id)) {
                    // make sure we dont overcount parameters
                    continue;
                }

                ids.add(parameter.id);
                let update = onParameterPatternChange(
                    this.sequencerEngine, parameter, oldPatternNumber, time);
                if (update) {
                    bulkUpdate[update.id] = update.value;
                }
           }

            let panningToBulk = this.setPanning(this.pattern.panning, undefined, true);
            return {
                bulkUpdate,
                panning: {
                    id: this.id,
                    panning: panningToBulk
                },
                volume: {
                    id: this.id,
                    volume: volumeToBulk
                },
                mute: {
                    id: this.id,
                    mute: muteToBulk
                }
            };
        }

        return {
            bulkUpdate: {},
            mute: {},
            volume: {},
            panning: {}
        };
    }

    getAllParameters(includeEffects=true) {
        let parameters = [... this.parameters, this.outputTypeParameter];
        for (let group of this.stepGroups) {
            for (let transformation of group.transformations) {
                parameters.push(transformation.thenValue);
                parameters.push(transformation.whenValue);
            }
        }

        if (this.trackData) {
            parameters = [... parameters, ... this.trackData.parameters];
        }
        for (let i in this.effectsChains) {
            parameters = [... parameters, ... this.effectsChains[i].getAllParameters()];
        }

        if (this.isFrozen) {
            parameters = [
                ... parameters,
                ... this.frozenColumns.map(x => x.gainParameter),
                ... this.frozenColumns.map(x => x.playingParameter)];
        }

        if (includeEffects) {
            parameters = [
                ... parameters,
                ... this.midiEffects.getAllParameters(),
                ... this.liveEffects.getAllParameters()];
        }

        if (this.getSamplers()[0]) {
            parameters = [
                ...parameters,
                ...this.getSamplers()[0].parameters
            ];
        }

        parameters = [
            ...parameters,
            ...parameters.filter(x => x.depthParameter).map(x => x.depthParameter)];

        return parameters;
    }

    getAllPatternLockedParameters(oldPattern, patternNumber) {
        return this.getAllParameters()
            .filter(p => p.hasPatternLockedValues(oldPattern, patternNumber));
    }

    deletePattern(i) {
        this.patterns.splice(i, 1);

        this.getAllPatternLockedParameters()
            .forEach(parameter => parameter.deletePattern(i));

        if (this.store.getState().tracks.trackPatternLocks[this.subSequence.id] === undefined) {
            return;
        }
        let patternLocks = {... this.store.getState().tracks.trackPatternLocks[this.subSequence.id][this.trackNumber] };
        this.store.dispatch(setTrackPatternLocks(this.subSequence.id, this.trackNumber, deleteKey(i, patternLocks)));
    }

    movePatternTo(patternFrom, patternTo) {
        if (patternFrom == patternTo) {
            return;
        }

        let newPatterns = [];
        if (patternFrom > patternTo) {
            newPatterns = this.patterns.slice(0, patternTo)
                .concat([this.patterns[patternFrom]])
                .concat(this.patterns.slice(patternTo, patternFrom));
            
            if (this.patterns.length > patternFrom + 1) {
                newPatterns = newPatterns.concat(this.patterns.slice(patternFrom + 1));;
            }
        } else {
            newPatterns = this.patterns.slice(0, patternFrom)
                .concat(this.patterns.slice(patternFrom + 1, patternTo))
                .concat([this.patterns[patternTo], this.patterns[patternFrom]]);
            
            if (this.patterns.length > patternTo + 1) {
                newPatterns = newPatterns.concat(this.patterns.slice(patternTo + 1));;
            }
        }

        this.patterns = newPatterns;

        this.getAllPatternLockedParameters()
            .forEach(parameter => parameter.movePatternTo(patternFrom, patternTo));

        if (!this.store.getState().tracks.trackPatternLocks[this.subSequence.id]) {
            return;
        }
        let patternLocks = {... this.store.getState().tracks.trackPatternLocks[this.subSequence.id][this.trackNumber]};
        this.store.dispatch(setTrackPatternLocks(
            this.subSequence.id,
            this.trackNumber, moveKey(patternFrom, patternTo, patternLocks)));
    }


    getStepAtResolution(step, resolution=this.resolution) {
        let steps = this.getStepsAtResolution(resolution);
        return steps[Math.floor(step) % steps.length];
    }

    // returns a list of steps (for the current pattern), using the 
    // given resolution.
    // So a resolution of 4, this function returns a list
    // of size 4 containing the steps at the quarter notes only
    getStepsAtResolution(resolution=baseResolution) {
        let pattern = this.getEasyPattern().pattern;
        let steps = pattern.getStepsAtResolution(resolution);
        return steps.slice(0, pattern.length(resolution));
    }

    getBucketsAtResolution(resolution) {
        return this.pattern.getBucketsAtResolution(resolution);
    }

    getFillsAtResolution(resolution) {
        return this.pattern.getFillsAtResolution(resolution);
    }
    
    // Returns a StepData object where all null parameters in the StepData
    // are replaced with the track's overall, default StepData.
    // This can be defined by the user by going to the track details page
    // and setting track-wide parameters
    getStepDataForStep(originalStepData, useCache=false) {
        let copiedStepData = originalStepData.getCopy(false, false, true, true, useCache);
        let polyphony = copiedStepData.getPolyphony();
        for (let i in polyphony) {
            let stepData = polyphony[i];
            stepData.duration = stepData.durationParameter.value == null ? this.trackData.duration : stepData.duration;
            stepData.transpose = stepData.transposeParameter.value == null ? this.trackData.transpose : stepData.transpose;
            stepData.velocity = stepData.velocityParameter.value == null ? this.trackData.velocity : stepData.velocity;
            stepData.start = stepData.startParameter.value == null ? this.trackData.start : stepData.start;
            stepData.chop = stepData.chopParameter.value == null ? this.trackData.chop : stepData.chop;
            stepData.chopDuration = stepData.chopDurationParameter.value == null ? this.trackData.chopDuration : stepData.chopDuration;
            stepData.attack = stepData.attackParameter.value == null ? this.trackData.attack : stepData.attack;
            if (this.getTrackType() !== INSTRUMENT) {
                stepData.release = stepData.releaseParameter.value == null ? this.trackData.release : stepData.release;
            } else {
                stepData.release = stepData.releaseParameter.value == null ? null : stepData.release;
            }
            stepData.slice = stepData.sliceParameter.value == null ? this.trackData.slice : stepData.slice;
            stepData.panning = stepData.panningParameter.value;
            stepData.pitchAttack = stepData.pitchAttackParameter.value == null ? this.trackData.pitchAttack: stepData.pitchAttack;
            stepData.pitchDecay = stepData.pitchDecayParameter.value  == null ? this.trackData.pitchDecay: stepData.pitchDecay;
            stepData.pitchSustain = stepData.pitchSustainParameter.value == null ? this.trackData.pitchSustain: stepData.pitchSustain;
            stepData.pitchRelease= stepData.pitchReleaseParameter.value == null ? this.trackData.pitchRelease: stepData.pitchRelease;
            stepData.monophonic = stepData.monophonicParameter.value == null ? this.trackData.monophonic : stepData.monophonic;
            stepData.reverse = stepData.reverse == null ? this.trackData.reverse : stepData.reverse;
            stepData.palette = stepData.palette == null ? this.trackData.palette : stepData.palette;
            stepData.mutate = stepData.mutateParameter.value == null ? this.trackData.mutate : stepData.mutate;
            stepData.auxEventA = stepData.auxEventA == null ? this.trackData.auxEventA: stepData.auxEventA;
            stepData.auxEventB = stepData.auxEventA == null ? this.trackData.auxEventB: stepData.auxEventB;
            stepData.auxEventC = stepData.auxEventA == null ? this.trackData.auxEventC: stepData.auxEventC;
            stepData.auxEventD = stepData.auxEventA == null ? this.trackData.auxEventD: stepData.auxEventD;
            stepData.effectAutomations = stepData.effectAutomations;
        }
        return copiedStepData;
    }

    getMainSoundId() {
        return this.soundIds[0];
    }

    calculateFills() {
        this.pattern.calculateFills(this.trackData);
    }

    updateStepDuration(step, duration, resolution) {
        resolution = resolution || baseResolution;
        var buckets = this.getBucketsAtResolution(resolution);
        for (var key in buckets[step].steps) {
            let stepData = buckets[step].getStep(key);
            if (stepData.on) {
                stepData.duration = duration;
            }
        }
        this.pattern.calculateFills(this.trackData);
        this.updateSteps();
    }

    doubleTrackLength() {
        let pattern = this.getEasyPattern().pattern;
        pattern.doubleTrackLength();
        pattern.calculateFills(this.trackData);

        this.updateSteps(true);
    }

    get length() {
        if (this.pattern === undefined) {
            return 16;
        }
        return this.pattern.length();
    }

    halfTrackLength() {
        let pattern = this.getEasyPattern().pattern;
        pattern.halfTrackLength();
        pattern.calculateFills(this.trackData);

        this.updateSteps(true);
    }

    getJson(skipFreeze=false) {
        var samplers = {};
        for (var i=0; i < this.soundIds.length; i++) {
            let sampler = this.samplers[this.soundIds[i]];
            if (sampler) {
                samplers[this.soundIds[i]] = sampler.getJson(); 
            }
        }

        let patternLocks = this.store.getState().tracks.trackPatternLocks[this.trackNumber] || {};
        let sendJson = [];

        for (let i in this.sendNodeVolumeParameters) {
            let parameter = this.sendNodeVolumeParameters[i];
            let json = {
                volume: parameter.get(),
            };
            json = saveParameterToJson(json, 'volumeParameter', parameter, true);
            sendJson.push(json);
        }

        let json = saveParameterToJson(
            {}, 'outputTypeParameter', this.outputTypeParameter);

        json = saveParameterToJson(
            json, 'skipMidiMasterParameter', this.skipMidiMasterParameter);

        let isFrozen = this.instrumentRack ? this.instrumentRack.isFrozen :
            this.isFrozen;

        json = saveParameterToJson(json, 'volumeParameter', this.volumeParameter, true);

        if (this.lfosTrack) {
            json.lfos = this.lfosTrack.lfos.map(
                lfo => lfo.getJson());
            json.envelopes = this.envelopesTrack.envelopes.map(
                env => env.getJson());
        }

        return {
            ... json,
            id: this.id,
            color: this._color,
            skipMidiMaster: this.skipMidiMasterParameter.get(),
            outputType: this.outputTypeParameter.get(),
            annotations: this.annotations.map(x => x.getJson()),
            isMuted: this.pattern.isMuted,
            sends: sendJson,
            isNewVolume: true,
            volume: this.pattern.volume,
            samplers: samplers,
            gated: this.gated,
            type: this.getTrackType(),
            patternLocks: patternLocks,
            envelopeConnections: this.connectedEnvelopes.filter(
                x => x && x.envelope)
                .map(x => x.getJson()),
            isFrozen: isFrozen,
            isFrozenPlaying: this.isFrozenPlaying,
            freeze: skipFreeze ? undefined : this.getFreezeJson(),
            chokes: this.chokes.getJson(),
            rawVolume: this.volumeNode.volume.value,
            presetName: this.presetName,
            stepGroups: this.stepGroups.map(
                group => getStepGroupJson(group)), // ({... group, steps: undefined})),
            ... this.getAuxJson()
        };
    }

    setPanning(panning, time, isBulk=false) {
        if (time !== undefined) {
            let currentTime = Tone.Master.context.isOffline ?
                Tone.Master.context._currentTime : 
                Tone.Master.context.currentTime; 
            this.gainNode.pan.linearRampToValueAtTime(
                panning, currentTime + time - 0.001);
            // this.gainNode.pan.rampTo(panning, time, Tone.Master.context.currentTime + 0.001);
        } else {
            this.gainNode.pan.value = panning;
        }
        this.gainNode.pan.value = panning;
        this.pattern.panning = panning;
        if (!isBulk) {
            this.store.dispatch(setTrackPanning(this.id, panning));
        }
        return panning;
    }

    getPanning() {
        return this.pattern.panning;
    }
    
    // steps the volume, passed as number from 0-100, using 10*log(volume) to calculate
    setVolume(volume, automation=false, isBulk=false, time) {
        if (!automation && !this.state.mix.isRecordingMix) {
            this.pattern.volume = volume;
        }

        if (!automation && !this.state.mix.isRecordingMix) {
            automation = true;
        }

        if (volume > 6) {
            volume = Math.max(4, getVolume(volume, 0, 128));
        }
        this.volumeParameter.set(volume, automation, true, time);
        if (!isBulk) {
            this.store.dispatch(setTrackVolume(this.id, volume));
        }
        return volume;
    }

    setRawVolume(volume) {
        this.volumeParameter.set(volume, true, true);
        this.pattern.volume = volume + 15;
    }

    mute(muted, automation=false, isBulk=false) {
        muted = muted === undefined ? true : muted;
        this.addEvent(new TrackMuteEvent(this, muted), automation);
        this.muted = muted;
        this.muteableNode.mute = muted;
        this.pattern.muted = muted;
        if (!isBulk) {
            this.store.dispatch(setTrackMuted(this.id, muted));
        }
        return muted;
    }

    moveEffectTo(effectFrom, effectTo) {
        this.effectsChains[this.chainNumber].moveEffectTo(effectFrom, effectTo);
    }

    moveMidiEffectTo(effectFrom, effectTo) {
        this.midiEffects.moveEffectTo(effectFrom, effectTo);
    }

    onBPMUpdated() {
        this.effectsChains.forEach(chain => chain.onBPMUpdated());
    }

    getEffectNumber(effect, isMidi=false) {
        if (isMidi) {
            return this.midiEffects.getEffectNumber(effect);
        } else {
            return this.effectsChains[this.chainNumber].getEffectNumber(effect);
        }
    }

    deleteEffect(effect) {
        this.effectsChains[this.chainNumber].deleteEffect(effect);
   }

    numberOfSlices() {
        var max = 0;
        for (var i in this.samplers) {
            if (this.samplers[i].slices.length > max) {
                max = this.samplers[i].slices.length;
            }
        }
        return max;
    }

    isLoaded() {
        return this.soundIds.length > 0 &&
            this.getSampler(0) !== undefined &&
            this.getSampler(0).isLoaded();
    }

    updateStepData(stepData, stepNumber, resolution) {
        let baseStepNumber = baseResolution
            .transformStepAtResolution(stepNumber, resolution);
        let stepIndex = Math.floor(baseStepNumber);
        let bucket = this.pattern.stepBuckets[stepIndex];
        
        for (var key in bucket.steps) {
            let oldStepData = bucket.getStep(key);
            if (oldStepData.on) {
                bucket.setStep(key, stepData, true);
                this.pattern.syncBuckets();
                break;
            }
        }

        this.updateSteps();
    }

    stepSelected(step) {
        this.selectedStep = step;
    }

    stepUnselected(step) {
        this.selectedStep = undefined;
    }

    isReversed() {
        return this.getSamplers()
            .filter(s => s !== undefined)
            .some(s => s.isReversed());
    }

    reverse(reversed, automation=false) {
        this.getSamplers()
            .forEach(s => s.reverse(reversed));
    }

    addEvent(event, automation=false) {
        if (this.mix === undefined) {
            return;
        }
        let state = this.store.getState();
        if (!automation && this.isRecordingAutomations()) {
            this.mix.addEvent(event);
        }
    }

    isRecordingAutomations() {
        return (this.state.sequencer.automationMode === RECORD &&
                this.state.sequencer.isRecording) ||
            this.state.mix.isRecordingMix;
    }

    setKeyboardEngine(engine) {
        this.keyboardEngine = engine;
    }

    updateSteps(syncBuckets=false, onlyUpdateSteps=false, isTriplet=false) {
        let pattern = this.pattern;
        if (this.state.arrangement.patternSelected) {
            pattern = this.patterns[this.state.arrangement.patternSelected.patternNumber];
            if (!pattern) {
                pattern = this.pattern;
            }
        }

        if (this.multiSequencerEngine ||
            this.sequencerEngine.context.isOffline) {
            return;
        }
        if (this.instrumentRack) {
            this.instrumentRack.updateSteps(syncBuckets, onlyUpdateSteps);
            return;
        }

        if (!onlyUpdateSteps) {
            this.store.dispatch(setTrackIsEnabled(
                this.subSequence.id, this.id, this.isEnabled));
        }
        let a = new Date().getTime();
        let action = pattern.getUpdateStepsAction(syncBuckets, isTriplet);
        let b = new Date().getTime();
        let trackPatternSteps = undefined;
        if (this.subSequence.isTrack) {
            trackPatternSteps = {};
            for (let i in this.patterns) {
                trackPatternSteps[i] = this.patterns[i].getTrackStepsAction();
            }
        }
        this.store.dispatch(
            setAllTrackSteps(
                action.id,
                action.steps,
                action.pianoRollSteps,
                action.trackFills,
                action.stepToId,
                action.pianoRollPositions,
                action.pianoRollDurations,
                trackPatternSteps,
                action.groups));
    }

    getUpdateStepsAction(syncBuckets=false, isTriplet=this.sequencerEngine.resolution.isTriplet) {
        return this.pattern.getUpdateStepsAction(syncBuckets, isTriplet);
    }

    get patternSelected() {
        return this.state.arrangement.patternSelected;
    }

    getEasyPattern() {
        let pattern = this.patternSelected ?
            this.patterns[this.patternSelected.patternNumber] :
            this.pattern;
        return new EasyPattern(pattern || this.pattern, this.trackData);
    }

    deleteMidiInput(input) {
        this.midiInputs.deleteMidiInput(input);
    }

    addMidiInput(input) {
        this.midiInputs.addMidiInput(input);
    }

    getInstrumentType() {
        return SAMPLER;
    }

    get state() {
        return this.store.getState();
    }

    dispatch(x) {
        this.sequencerEngine.dispatch(x);
    }

    /*
    clearEffectsAndSamplers() {
        // dispose all the effects
        this.effectsChains.forEach(chain => chain.dispose());
        this.midiEffects.effects = [];
        this.liveEffects.effects = [];
        this.samplers = {};
        let samplers = this.getSamplers(); 
        this.soundIds = [];
        samplers
            .filter(s => s !== undefined)
            .forEach(s => s.dispose());

        // default track data again
        this.trackData = new StepData(
            this.sequencerEngine,
            this.mix,
            new ParameterContext().forTrack(this));
        this.trackData.loadDefaults();
    }
    */

    fromJson(json, isPreset) {
        this.cachedPlayhead = undefined;
        if (json.id) {
            if (!this.sequencerEngine.trackIDExists(
                json.id, this)) {
                this.id = json.id;
                this.sequencerEngine.idToObject[this.id] = this;
            }
        }
        if (json.isFrozen !== undefined) {
            this.isFrozen = json.isFrozen;
        }

        if (json.freeze) {
            this.loadFreezeJson(json.freeze);
        } 

        if (json.outputType) {
            this.outputTypeParameter.set(json.outputType);
        }

        if (json.color) {
            this.color = json.color;
        }

        if (json.skipMidiMaster) {
            this.skipMidiMasterParameter.set(json.skipMidiMaster);
        }

        if (json.skipMidiMaster === undefined && json.isFrozen) {
            this.skipMidiMasterParameter.set(false);
        }

        if (json.skipMidiMasterParameter) {
            loadParameterFromJson(json, 'skipMidiMasterParameter', this.skipMidiMasterParameter);
        }

        if (json.stepGroups) {
            this.stepGroups = json.stepGroups.map(g => fromStepGroupJson(g, this));
        }

        loadParameterFromJson(json, 'outputTypeParameter', this.outputTypeParameter);

        if (json.isFrozenPlaying !== undefined) {
            this.isFrozenPlaying = json.isFrozenPlaying;
        }

        this.frozenJson = json.frozenJson;
        this.currentPattern = this.getInitialPattern();

        let bulk =  {};
        if (!isPreset) {
            bulk = loadParameterFromJson(json, "volumeParameter", this.volumeParameter);
        }

        if (json.sends) {
            for (let i in json.sends) {
                if (this.sendNodeVolumeParameters[i]) {
                    this.sendNodeVolumeParameters[i].set(json.sends[i].volume);
                    bulk = {
                        ... bulk,
                        ... loadParameterFromJson(
                            json.sends[i], 'volumeParameter',
                            this.sendNodeVolumeParameters[i])
                    };
                }
            }
        }
        this.store.dispatch(setEffectParameterValues(bulk));

        this.presetName = json.presetName;

        if (json.auxEventATypeParameter) {
            this.fromAuxJson(json);
        }

        if (json.lfos && this.lfosTrack) {
            this.lfosTrack.lfos.map(
                (lfo, i) => lfo.lfo.fromJson(
                    json.lfos[i]));

            this.envelopesTrack.envelopes.map(
                (envelope, i) => envelope.envelope.fromJson(
                    json.envelopes[i]));
        }
    }

    get tempo() {
        return this.getSamplers()[0] === undefined ? undefined : this.getSamplers()[0].tempo;
    }

    warp() {
        let effect = this.midiEffects.effects.filter(
            x => x.name === 'Warp')[0];

        if (effect) {
            this.operationManager.dispatch(
                toggleEffect(effect, this));
        } else {
            this.operationManager.dispatch(
                addEffect(new WarpEffect(this), this));
        }

        // set slice to first
        // get first slice

        if (this.getSamplers()[0]) {
            this.getSamplers()[0].sliceFirstOnset();
        }
    }

    isWarped() {
        return this.midiEffects.effects.filter(
            e => e.name === 'Warp')
            .filter(e => e.enabled)
            .length > 0;
    }

    async mateWithPreset(preset, probability=0.5, crossbreedEffects) {
        let data = await presets.getPresetData(preset.contentHash);
        for (let i in data.lfos) {
            this.sequencerEngine.lfos[i].lfo.mateJson(data.lfos[i], probability);
        }
        
        this.instrument.mateJson(data.trackParameters.instrument, probability);

        if (crossbreedEffects) {
            let ret = await this.sequencerEngine.getEffectsPreset(data, this);
            this.effectsChains[0].mate(ret.effectsChains[0], probability);
            for (let i=1; i < ret.effectsChains.length; i++) {
                ret.effectsChains[i].dispose();
            }
            if (ret.midiEffects) {
                this.midiEffects.mate(ret.midiEffects, probability);
            }
        }

        this.sequencerEngine.update();
        return;
    }

    extractStems() {
        return new Promise((resolve) => {
            let buffer = this.sample.audioBuffer;
            let buffers = [buffer.getChannelData(0), buffer.getChannelData(1)];
            this.sequencerEngine.dispatch(setLoadingText("Extracting stems (might take a few)"));
            new SoundUploader(2).exportToWAV(buffers, async (wav) => {
                let id = await ipfs.addBytesArray(wav);
                fetch('/splitStems/' + id , {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    }
                })
                    .then(response => response.json())
                    .then(async resp => {
                        let drums = resp.drums ? this.sample.getCopyWith(resp.drums, 'drums') : undefined;
                        let vocals = resp.vocals ? this.sample.getCopyWith(resp.vocals, 'vocals') : undefined;
                        let bass = resp.bass ? this.sample.getCopyWith(resp.bass, 'bass'): undefined;
                        let other = resp.other ? this.sample.getCopyWith(resp.other, 'other') : undefined;
                        
                        let promises = [drums, vocals, bass, other]
                            .filter(x => x !== undefined)
                            .map(p => p.fetchAudio());
                        await Promise.all(promises);
                        this.sequencerEngine.dispatch(setLoadingText(undefined));
                        
                        if (drums) {
                            this.sequencerEngine.addSound(drums);
                            this.sequencerEngine.addSampleToSync(drums);
                        }
                        if (vocals) {
                            this.sequencerEngine.addSound(vocals);
                            this.sequencerEngine.addSampleToSync(vocals);
                        }
                        if (bass) {
                            this.sequencerEngine.addSound(bass);
                            this.sequencerEngine.addSampleToSync(bass);
                        }
                        if (other) {
                            this.sequencerEngine.addSound(other);
                            this.sequencerEngine.addSampleToSync(other);
                        }
                        
                        resolve();
                    });
            });
        });
    }

    async extractGroove() {
        let sampler = this.getSamplers()[0];
        let onsets = sampler.onsets;
        let tempo = sampler.tempo;

        let firstSlice = sampler.slices[0].startTime;
        onsets = onsets.filter(x => x / 1000.0 >= firstSlice);

        let groove = extractGroove(onsets, tempo, this.sequencerEngine.bpm);
        let sampleId = 'QmRHeFU644J9LFZoPzd93M4WqF8tyhydDYL2EZWS6dmbF8';
        let track = await this.sequencerEngine.addSound(sampleId);
        let pattern = track.getEasyPattern();
        groove.steps.forEach(
            hit =>
                pattern.addStep(
                    track.getNewStep(),
                    hit.stepNumber,
                    baseResolution,
                    hit.offset,
                    false,
                    true));

        for (let i in groove.timings) {
            let resolution = groove.timings[i].resolution;
            let odd = groove.timings[i].odd;
            let even = groove.timings[i].even;
            let timingEffect = new TimingEffect(track);
            timingEffect.oddOffset = odd;
            timingEffect.evenOffset = even;
            timingEffect.resolution = 32 / resolution;

            track.midiEffects.addEffect(timingEffect);
        }

        pattern.update(false);
        track.updateSteps();
    }

    getNewStep() {
        let step = new StepData(this.sequencerEngine, undefined, undefined, this);
        step.on = true;
        return step;
    }

    showEffects(show) {
        this.store.dispatch(setShowTrackEffects(this.trackNumber, show));
    }

    isEffectsShowing() {
        return this.state.spatial.showEditorBottom === EFFECTS;
    }

    isHidden() {
        let tracksToHide =  this.state.tracks.tracksToHide;
        return tracksToHide.indexOf(this) > -1;
    }

    restart() {
    }

    onStepEnded() {
    }

    onNewStep(step) {
        this.midiEffects.onNewStep();
    }

    getInitialPattern() {
        if (!this.subSequence.isTrack) {
            return 0;
        }

        return undefined;
    }

    scheduleTrackPatternChangeIfAny(step=this.sequencerEngine.trackStep, force=false) {
        if (!this.subSequence.isTrack) {
            return;
        }
        if (this.patterns.some(s => s.isRecording)) {
            return;
        }
        for (let i in this.patterns) {
            i = parseInt(i);
            let pattern = this.patterns[i];
            let {startStep} = pattern;
            if (startStep === undefined) {
                continue;
            }

            if (step >= startStep && step < startStep + pattern.length()) {
                if (i !== this.currentPattern) {
                    this.switchToPattern(i);
                }
                return;
            }
        }

        this.currentPattern = undefined;
        this.pattern = this.patterns[0];
    }

    async getGuildIds() {
        if (this.sample === undefined) {
            return [0];
        }
        let guildId = await ethereum.guildSamples.getGuildIdForSound(this.sample.getId());
        return guildId === undefined ? [0] : [guildId];
    }

    async makePublicIfNeeded() {
        let guildIds = await this.getGuildIds();
        if (guildIds[0] !== 0 && this.sample) {
            // need to make public
            // console.log("saving sample = ", this);
            let id = await ethereum.guildSamples.saveSample(this.sample);
            this.replaceSoundId(this.sample.getId(), id);
        }

        for (let i in this.effectsChains) {
            await this.effectsChains[i].makePublicIfNeeded();
        }
    }

    replaceSoundId(fromId, toId) {
        let index = this.soundIds.indexOf(fromId);
        if (index === -1) {
            return;
        }

        this.soundIds[index] = toId;
        let sampler = this.samplers[this.sample.getId()];
        this.samplers[toId] = sampler;
        this.sample.ipfsHash = toId;
        delete this.samplers[fromId];
    }

    get operationManager() {
        return this.sequencerEngine.operationManager;
    }

    deleteAtStep(step) {
        this.pattern.deleteStepNumber(step);
    }

    isMonophonic() {
        return false;
    }

    getIdentifier() {
        let track = this;
        let _track = track;
        if (track && track.tracks && track.tracks[0]) {
            _track = track.tracks[0].track;
        }
        return _track.presetName ? _track.presetName :
            _track.sample !== undefined && _track.sample.getId() !== undefined ?
            _track.sample.getId() :
            _track.getName();
    }

    addRackEffect(... args) {
        super.addRackEffect(... args);
        this.updateMidiEffectsRedux();
    }

    updateEffectRedux(effects) {
        this.updateMidiEffectsRedux();
    }

    updateMidiEffectsRedux(effects=this.effectRack && this.effectRack.effects) {
        if (this.effectRack) {
            this.dispatch(
                setTrackMidiEffects(
                    this.id,
                    [... effects]));
        }
    }

    get effectRack() {
        let midiFx = this.midiEffects.effects;
        return midiFx.find(e => e.name === "Midi Effect Rack");
    }

    getParameters() {
        return [];
    }

    sendLevels(value) {
        if (this.levelsInterval) {
            clearInterval(this.levelsInterval);
        };

        if (value) {
            // this.trackMeter.smoothing = 0.1;
            this.levelsInterval = setInterval(() => {
                if (this.sequencerEngine.isLoading) {
                    return;
                }
                let x = this.trackMeter.getLevel();
                this.sendMessage("levels data", x < -80 ? 0 : Math.pow((x + 80)/86, 2));
            }, 20);
        }
    }

    async saveNewSample() {
        await this.sample.saveBuffer();
        let id = this.sample.ipfsHash;
        let sampler = this.samplers["NOT_UPLOADED_YET"];
        this.soundIds = [id];
        this.samplers[id] = sampler;
    }

    scheduleAnyRecording(patternStep) {
    }
}

export default {Track, baseResolution, oldDefaultResolution};

function getBaseLog(x, y) {
    return Math.log(y) / Math.log(x);
}
